import { useMemo, useState } from 'react'
import { Link, createFileRoute, useNavigate } from '@tanstack/react-router'
import { FileSpreadsheet, Layers, Plus, Printer } from 'lucide-react'
import { toast } from 'sonner'

import type { ListLotsParams } from '@/lib/api/packaging-lot-api'
import { packagingLotApi } from '@/lib/api/packaging-lot-api'
import {
    downloadBlob,
    filenameFromContentDisposition,
} from '@/lib/download-blob'
import { ClearFilterButton } from '@/components/common/clear-filter-button'
import { defineListFilters } from '@/components/common/filters/core'
import { positionFilter } from '@/components/common/filters/position-filter'
import { textFilter } from '@/components/common/filters/text-filter'
import { useListFilters } from '@/components/common/filters/use-list-filters'
import { ListFilterBar } from '@/components/common/ListFilterBar'
import { ListPageHeader } from '@/components/common/ListPageHeader'
import {
    LOT_CARD_CODE_CLASS,
    LotListCard,
} from '@/components/common/LotListCard'
import { Button } from '@/components/ui/button'
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@/components/ui/table'
import { useIsMobile } from '@/hooks/use-is-mobile'
import { useWarehousePosition } from '@/hooks/use-warehouse-position-query'
import { usePrintPackagingLotLabel } from '@/hooks/use-lot-label-print'
import {
    usePackagingLots,
    usePackagingLotsInfinite,
} from '@/hooks/use-packaging-lot-query'
import { formatDateTimeVN, formatQty } from '@/lib/format'
import { m } from '@/lib/paraglide/messages'

/**
 * Bộ lọc khai một lần, suy ra: parser URL, mặc định, badge đếm, chip.
 *
 * `textFilter` thay `PackagingLotSearchInput` — component đó tồn tại chỉ để tự
 * lo debounce, đúng việc mà factory chung đã làm.
 */
const FILTERS = defineListFilters({
    q: textFilter({
        label: m.pkl_lots_search_label,
        placeholder: m.pkl_lots_search_placeholder,
    }),
    position_id: positionFilter({
        label: m.lot_list_filter_position,
        lotType: 'packaging',
        placeholder: m.lot_list_filter_all_positions,
    }),
})

export const Route = createFileRoute(
    '/_app/_authed/_sidebar/packaging-warehouse/lots/',
)({
    component: Page,
    validateSearch: FILTERS.validateSearch,
})

function Page() {
    const navigate = useNavigate({ from: '/packaging-warehouse/lots/' })
    const urlSearch = Route.useSearch()

    // Chip lọc theo vị trí cần TÊN, URL chỉ có id → tra runtime rồi truyền.
    const positionId =
        typeof urlSearch.position_id === 'string' ? urlSearch.position_id : ''
    const { data: selectedPosition } = useWarehousePosition(
        positionId || undefined,
    )
    const positionLabel = selectedPosition?.data
        ? (selectedPosition.data.name ?? selectedPosition.data.code)
        : undefined

    const { values, barProps, page, setPage } = useListFilters({
        def: FILTERS,
        search: urlSearch,
        onSearchChange: (next, opts) =>
            void navigate({
                search: () => next,
                replace: opts?.replace ?? false,
            }),
        columns: 3,
        chipLabels: {
            position_id: positionLabel
                ? `${m.lot_list_filter_position()}: ${positionLabel}`
                : undefined,
        },
    })

    // Map bộ lọc → tham số API. Để tường minh ở màn: mỗi màn map một kiểu.
    const apiFilters = useMemo(
        () => ({
            exclude_virtual: '1' as const,
            ...(values.q ? { search: values.q } : {}),
            ...(values.position_id ? { position_id: values.position_id } : {}),
        }),
        [values.q, values.position_id],
    )

    const params = useMemo<ListLotsParams>(
        () => ({ page, per_page: 50, ...apiFilters }),
        [page, apiFilters],
    )
    const isMobile = useIsMobile()

    const { data, isLoading, isFetching, refetch } = usePackagingLots(params, {
        enabled: !isMobile,
    })

    // Mobile: infinite (params bỏ page — hook tự quản)
    const infiniteParams = useMemo(
        () => ({ per_page: 50, ...apiFilters }),
        [apiFilters],
    )
    const infinite = usePackagingLotsInfinite(infiniteParams, {
        enabled: isMobile,
    })
    const mobileLots = useMemo(
        () => infinite.data?.pages.flatMap((p) => p.data ?? []) ?? [],
        [infinite.data],
    )

    const lots = data?.data ?? []
    const total = data?.meta?.total ?? 0

    // Rỗng vì chưa có dữ liệu khác hẳn rỗng vì bộ lọc quá hẹp — trường hợp thứ
    // hai phải cho người dùng lối thoát ngay tại chỗ (R10).
    const filtering = barProps.activeCount > 0 || !!values.q
    const emptyState = (
        <div className="flex flex-col items-center gap-3 py-8 text-center">
            <span className="text-muted-foreground">
                {filtering ? m.list_empty_filtered() : m.pkl_lots_empty()}
            </span>
            {filtering ? (
                <ClearFilterButton active onClear={barProps.onClear} />
            ) : null}
        </div>
    )
    const printMut = usePrintPackagingLotLabel()

    // Xuất Excel theo filter đang áp (BE tự quét toàn bộ lô còn khả dụng).
    const [exporting, setExporting] = useState(false)
    const handleExport = async () => {
        setExporting(true)
        try {
            const res = await packagingLotApi.exportExcel(params)
            downloadBlob(
                res.data,
                filenameFromContentDisposition(
                    res.headers['content-disposition'] as string | undefined,
                ) ?? 'lo-vat-tu.xlsx',
            )
        } catch {
            toast.error(m.lot_list_export_failed())
        } finally {
            setExporting(false)
        }
    }

    // debounce=true cho desktop (gõ tới đâu tìm tới đó), false cho mobile —
    // giữ nguyên hành vi cũ là chỉ tìm khi nhấn Enter.
    return (
        <div className="space-y-4 page-pad">
            <ListPageHeader
                icon={<Layers className="size-5 shrink-0" />}
                title={m.pkl_lots_title()}
                utilityActions={
                    <>
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={() => void handleExport()}
                            disabled={exporting}
                            title={m.lot_list_export_btn()}
                            aria-label={m.lot_list_export_btn()}
                        >
                            <FileSpreadsheet className="size-4 md:mr-1" />
                            <span className="hidden md:inline">
                                {exporting
                                    ? m.lot_list_exporting()
                                    : m.lot_list_export_btn()}
                            </span>
                        </Button>
                    </>
                }
                primaryActions={
                    <Button asChild>
                        <Link
                            to="/packaging-warehouse/stock-in-notes/new"
                            className="w-full"
                        >
                            <Plus className="size-4 mr-1" />
                            {m.pkl_lots_create_pnk()}
                        </Link>
                    </Button>
                }
            />

            <ListFilterBar
                {...barProps}
                onRefresh={() => void refetch()}
                refreshing={isFetching}
            />

            {/* Mobile: card list + Xem thêm */}
            <div className="space-y-2 md:hidden">
                {infinite.isLoading ? (
                    <div className="py-8 text-center text-muted-foreground">
                        {m.sl_lots_loading()}
                    </div>
                ) : mobileLots.length === 0 ? (
                    emptyState
                ) : (
                    <>
                        {mobileLots.map((lot) => {
                            const attributeSub = [
                                lot.warehouse_name,
                                lot.position_code,
                            ]
                                .filter(Boolean)
                                .join(' · ')
                            return (
                                <LotListCard
                                    key={lot.id}
                                    onClick={() =>
                                        void navigate({
                                            to: '/packaging-warehouse/lots/$id',
                                            params: { id: lot.id },
                                        })
                                    }
                                    code={
                                        <Link
                                            to="/packaging-warehouse/lots/$id"
                                            params={{ id: lot.id }}
                                            className={LOT_CARD_CODE_CLASS}
                                        >
                                            {lot.code}
                                        </Link>
                                    }
                                    attribute={lot.material_name ?? '—'}
                                    attributeSub={
                                        attributeSub ? attributeSub : undefined
                                    }
                                    quantityAvailable={lot.quantity_available}
                                    quantityUnit={lot.material_unit}
                                    createdAt={lot.created_at}
                                    actions={
                                        <Button
                                            size="sm"
                                            variant="ghost"
                                            aria-label={m.lot_label_print_btn()}
                                            title={m.lot_label_print_btn()}
                                            disabled={printMut.isPending}
                                            onClick={() =>
                                                printMut.mutate(lot.id)
                                            }
                                        >
                                            <Printer className="size-4" />
                                        </Button>
                                    }
                                />
                            )
                        })}
                        {infinite.hasNextPage && (
                            <Button
                                variant="outline"
                                className="w-full h-11"
                                disabled={infinite.isFetchingNextPage}
                                onClick={() => void infinite.fetchNextPage()}
                            >
                                {infinite.isFetchingNextPage
                                    ? m.list_loading_more()
                                    : m.list_load_more()}
                            </Button>
                        )}
                    </>
                )}
            </div>

            {/* Desktop: table */}
            <div className="hidden md:block rounded-md border bg-card overflow-x-auto">
                <Table>
                    <TableHeader>
                        <TableRow>
                            <TableHead>{m.sl_lots_col_code()}</TableHead>
                            <TableHead>{m.pkl_lots_col_material()}</TableHead>
                            <TableHead>{m.pkl_lots_col_warehouse()}</TableHead>
                            <TableHead>{m.lot_list_col_position()}</TableHead>
                            <TableHead className="text-right">
                                {m.sl_lots_col_available()}
                            </TableHead>
                            <TableHead>{m.sl_lots_col_created_at()}</TableHead>
                            <TableHead className="w-[80px]"></TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {isLoading ? (
                            <TableRow>
                                <TableCell
                                    colSpan={7}
                                    className="py-8 text-center text-muted-foreground"
                                >
                                    {m.sl_lots_loading()}
                                </TableCell>
                            </TableRow>
                        ) : lots.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={7}>{emptyState}</TableCell>
                            </TableRow>
                        ) : (
                            lots.map((lot) => (
                                <TableRow
                                    key={lot.id}
                                    className="cursor-pointer hover:bg-muted/50"
                                    onClick={() =>
                                        void navigate({
                                            to: '/packaging-warehouse/lots/$id',
                                            params: { id: lot.id },
                                        })
                                    }
                                >
                                    <TableCell className="font-mono text-sm">
                                        <Link
                                            to="/packaging-warehouse/lots/$id"
                                            params={{ id: lot.id }}
                                            className="text-primary hover:underline"
                                        >
                                            {lot.code}
                                        </Link>
                                    </TableCell>
                                    <TableCell>{lot.material_name}</TableCell>
                                    <TableCell>{lot.warehouse_name}</TableCell>
                                    <TableCell className="text-sm">
                                        {lot.position_code ?? '—'}
                                    </TableCell>
                                    <TableCell className="text-right tabular-nums">
                                        {formatQty(lot.quantity_available)}
                                    </TableCell>
                                    <TableCell className="text-sm text-muted-foreground">
                                        {formatDateTimeVN(lot.created_at)}
                                    </TableCell>
                                    <TableCell
                                        className="text-right"
                                        onClick={(e) => e.stopPropagation()}
                                    >
                                        <Button
                                            size="sm"
                                            variant="ghost"
                                            title={m.lot_label_print_btn()}
                                            disabled={printMut.isPending}
                                            onClick={() =>
                                                printMut.mutate(lot.id)
                                            }
                                        >
                                            <Printer className="size-4" />
                                        </Button>
                                    </TableCell>
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
            </div>

            {/* Pagination — desktop/tablet only */}
            <div className="hidden md:flex items-center justify-between">
                <Button
                    variant="outline"
                    size="sm"
                    disabled={page === 1}
                    onClick={() => setPage(Math.max(1, page - 1))}
                >
                    {m.sl_pagination_prev()}
                </Button>
                <div className="text-sm text-muted-foreground">
                    {m.sl_pagination_page({
                        page,
                        total: Math.max(1, Math.ceil(total / 50)),
                    })}
                </div>
                <Button
                    variant="outline"
                    size="sm"
                    disabled={!total || page * 50 >= total}
                    onClick={() => setPage(page + 1)}
                >
                    {m.sl_pagination_next()}
                </Button>
            </div>
        </div>
    )
}
